import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, ShieldCheck, Sparkles } from "lucide-react";

export default function About() {
  usePageTitle("Who We Are — Central Rentals Canada");
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="about-hero">
          <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600">Who We Are</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 mt-3">
            Built by landlords, for landlords.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            Central Rentals Canada exists for one reason — Canadian
            landlords deserve software that speaks their language. Every province,
            every form, every tax line, built in-country and supported by people
            who actually run rentals.
          </p>
          <a
            href="https://maps.app.goo.gl/3R94HfmbLnbNgUvk9?g_st=ic"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-gbp-link"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 text-sm font-semibold text-slate-800 transition-colors"
          >
            <MapPin className="w-4 h-4 text-emerald-600"/> Find Central Rentals Canada on Google Maps
          </a>
        </div>
      </section>

      {/* Story + Mission */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Our story</div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">Founded 2009. Launched 2016.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Central Rentals Canada was founded in 2009 by property-management
              professionals who gained first-hand experience understanding the
              specific needs of Canadian landlords. Recognizing the challenges
              faced by small landlords, the company launched publicly in 2016 to
              offer essential tools and resources tailored to their requirements.
            </p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Our mission</div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">The premier digital landlord kit.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We provide a premier digital landlord kit designed specifically for
              apartment communities — fostering seamless connections among
              landlords, property-management companies, and multifamily
              operators. Priced for real landlords, built for every Canadian
              province, and never taking a cut of your rent.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-slate-50 border-y border-slate-200 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600 text-center">What we believe</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 text-center mt-2">
            Four values that drive every decision.
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {[
              { icon: MapPin, t: "Canadian-first", d: "Every form, every clause, every tax line built for Canadian rules — not retrofitted from a US product." },
              { icon: Users, t: "People before pixels", d: "Real humans answer the phone. Real landlords shape the roadmap. We ship features customers actually asked for." },
              { icon: ShieldCheck, t: "Boring is beautiful", d: "Rent collection should be invisible. Inspections should be one tap. Boring on the inside means quiet on your phone." },
              { icon: Sparkles, t: "Fast > fancy", d: "Pages load in under a second. Stripe checkout opens in two taps. We measure speed in milliseconds, not minutes." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`about-value-${t.toLowerCase().split(" ")[0]}`}>
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="font-display text-lg font-bold text-slate-900">{t}</div>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack section removed at owner's request — we no longer
         advertise specific vendor/model brand names on the About page. */}

      {/* CTA */}
      <section className="py-12 md:py-16 border-t border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-2xl md:text-4xl font-extrabold tracking-tighter text-slate-900">
            Come run your rentals with us.
          </h2>
          <p className="mt-3 text-slate-600">Start your Paid plans from CA$35/month — no card required to see the dashboard.</p>
          <Link to="/signup?role=landlord" data-testid="about-cta-signup"
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-600 text-white font-bold text-base shadow-lg hover:bg-emerald-700 hover:shadow-xl transition-all">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>
    </div>
  );
}
